<template>
  <div class="sf-level-item">
    <div class="sf-level">
      <div
        class="
          sf-level-item
          tw-w-7
          tw-h-7
          tw-flex
          tw-items-center
          tw-justify-center
          tw-bg-sf-neutral-subtle-2
          tw-rounded
        "
      >
        <span class="fa fa-link sf-font-m"></span>
      </div>
      <div
        class="sf-level-item !tw-mr-2"
        :data-walkthrough-id="`company-label-${companyId}`"
      >
        <span class="sf-font-medium sf-pad-r-1 tw-text-sf-gray-dark">
          {{ displayCompanyName }}
        </span>
        <span
          v-if="ledgerLabel && !inPeriod"
          v-dompurify-html="
            I18n.t('company_links.missing_external_period_error', {
              period: ledgerLabel,
            })
          "
          v-tooltip.html="missingPeriodTooltip"
          class="sf-label sf-label-danger sf-label-active"
        ></span>
        <span
          v-else-if="inPeriod && showSelectedLabel"
          class="sf-label sf-label-success"
        >
          {{ I18n.t('company_links.selected_company_label') }}
        </span>
        <div v-if="lastSyncedAt">
          <i class="fa fa-refresh sf-font-xs sf-c-text-light"></i>
          <span class="sf-c-text-light sf-font-xs">
            {{
              I18n.t('company_links.last_synced_on', {
                date: displaySyncDate,
              })
            }}
          </span>
        </div>
        <div
          v-if="createdBy && createdAt"
          class="tw-text-sf-gray-light tw-leading-[14px] sf-font-xs"
        >
          {{
            I18n.t('company_links.created_by', {
              user_name: createdBy,
              date: displayCreateAtDate,
            })
          }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import formatTimestamp from '@app/utils/time-formatter'
import { useCompanyLinksStore } from '@app/application/company_links/store/use-company-links'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

const I18n = useI18n()

interface Props {
  companyId: number
  companyName: string
  createdBy?: string
  createdAt?: string
  lastSyncedAt?: string
  ledgerLabel?: string
  showSelectedLabel?: boolean
}

const { companyId, companyName, createdBy, createdAt, lastSyncedAt } =
  defineProps<Props>()

const companyLinksStore = useCompanyLinksStore()
const { initialized, linkedCompaniesWithPeriod } =
  storeToRefs(companyLinksStore)

const loadCompanyLinksStore = async () => {
  if (!initialized.value) {
    await companyLinksStore.initExistingCompanyLinks()
  }
}
loadCompanyLinksStore()

const displayCompanyName = computed(() => {
  if (!companyName) return ''

  const allowedLength = 40
  return companyName.length > allowedLength
    ? `${companyName.slice(0, allowedLength)}...`
    : companyName
})

const inPeriod = computed(() => {
  return linkedCompaniesWithPeriod.value.includes(companyId)
})

const displaySyncDate = computed(() => {
  if (!lastSyncedAt) return undefined

  return formatTimestamp(lastSyncedAt, {
    shouldUseLocalTimezone: true,
    format: 'D MMM Y, HH:mm z',
  })
})

const displayCreateAtDate = computed(() => {
  if (!createdAt) return undefined

  return formatTimestamp(createdAt, {
    shouldUseLocalTimezone: true,
    format: 'D MMM Y',
  })
})

const missingPeriodTooltip = computed(() => {
  return I18n.t('company_links.missing_external_period_error_tooltip', {
    company: displayCompanyName.value,
  })
})
</script>
